import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiHelpCircle } from 'react-icons/fi';
import {
  Button,
  Modal,
  HelpWindow,
  multiAnalysisHelpTopics,
} from '../../design-system';
import { Sample } from '../../@types/samples/types';
import { Well } from '../../@types/wells/types';
import { useAppContext } from '../../providers/AppContext';
import ProjectShell from '../../components/layout/ProjectShell';
import LoadSampleModal from '../CapillaryPressurePage/LoadSampleModal';
import { ReactComponent as EmptyChartIllustration } from '../../assets/empty-chart-capillary-pressure.svg';
import { useMultiAnalysis } from '../../providers/MultiAnalysis';
import { METHOD_LABELS } from './types';
import { useLiaoWorkerQueue } from './useLiaoWorkerQueue';
import SelectionToolbar from './components/SelectionToolbar';
import ThumbnailGrid from './components/ThumbnailGrid';
import BatchControlsBar from './components/BatchControlsBar';
import AutoCorrelationCta from './components/AutoCorrelationCta';
import SampleDetailPanel from './components/SampleDetailPanel';
import styles from './MultiAnalysisPage.module.css';

type IconProps = { size?: number; color?: string; style?: React.CSSProperties };
const HelpIcon = FiHelpCircle as React.FC<IconProps>;

/**
 * Multi Analysis — batch Liao capillary pressure workflow.
 *
 * Select many samples, run the Liao solver for all of them in one action,
 * review per-sample validation statuses in an IP-style thumbnail grid, and
 * inspect/rerun individual samples without leaving the batch context.
 */
const MultiAnalysisPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [columns, setColumns] = useState(4);
  const [chartScale, setChartScale] = useState<'linear' | 'log'>('linear');
  const location = useLocation();
  const projectUid = new URLSearchParams(location.search).get('project');

  const { data } = useAppContext();
  const analysis = useMultiAnalysis();
  const { regenerateCurves } = useLiaoWorkerQueue();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setModalOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelectSample = useCallback(
    (sample: Sample, _allSamples: Sample[], _well?: Well) => {
      analysis.addToSelection([sample]);
      setModalOpen(false);
    },
    [analysis.addToSelection]
  );

  const handleSelectMany = useCallback(
    (picked: Sample[], _allSamples: Sample[]) => {
      analysis.addToSelection(picked);
      setModalOpen(false);
    },
    [analysis.addToSelection]
  );

  // Prefer the previewed (computed) sample so the detail chart/params show the
  // batch result — the stored sample has no analysis until saved. Falls back
  // to the stored sample when there is no preview yet for the active method.
  const inspectedSample = analysis.inspectedUid
    ? analysis.results.get(analysis.inspectedUid)?.sample ??
      analysis.samplesByUid.get(analysis.inspectedUid)
    : undefined;

  /** Prev/next stepping across the currently visible (filtered) samples. */
  const inspectedIndex = useMemo(
    () =>
      analysis.inspectedUid
        ? analysis.visibleSamples.findIndex(
            sample => sample.uid === analysis.inspectedUid
          )
        : -1,
    [analysis.inspectedUid, analysis.visibleSamples]
  );

  const stepInspection = useCallback(
    (offset: number) => {
      const next = analysis.visibleSamples[inspectedIndex + offset];
      if (next) analysis.setInspectedUid(next.uid);
    },
    [analysis.visibleSamples, inspectedIndex, analysis.setInspectedUid]
  );

  const hasSelection = analysis.samples.length > 0;
  const running = analysis.batchStatus === 'running';

  // Post-save CTA: once at least one previewed result has been persisted,
  // offer to run the Auto Correlation over these samples.
  const hasSavedResults = useMemo(
    () =>
      Array.from(analysis.results.values()).some(result => result.saved),
    [analysis.results]
  );

  // Persisting overwrites stored analyses — confirm first if any would.
  const handleSave = useCallback(() => {
    if (analysis.overwriteUids.length > 0) {
      setConfirmSaveOpen(true);
    } else {
      analysis.saveChecked();
    }
  }, [analysis.overwriteUids, analysis.saveChecked]);

  const confirmSave = useCallback(() => {
    setConfirmSaveOpen(false);
    analysis.saveChecked();
  }, [analysis.saveChecked]);

  return (
    <ProjectShell>
      <div className={styles.stickyBar}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Multi Analysis</h1>
            <p className={styles.subtitle}>
              Batch capillary pressure analysis (Liao / Brooks-Corey) with
              per-sample validation
            </p>
          </div>
          <Button
            variant="secondary"
            startIcon={<HelpIcon size={14} />}
            onClick={() => setHelpOpen(true)}
          >
            Help
          </Button>
        </div>

        <SelectionToolbar
          projectUid={projectUid}
          wells={data.wells}
          sampleCount={analysis.samples.length}
          method={analysis.method}
          onMethodChange={analysis.setMethod}
          lambdaMethod={analysis.lambdaMethod}
          onLambdaMethodChange={analysis.setLambdaMethod}
          compareAllMethods={analysis.compareAllMethods}
          onCompareAllMethodsChange={analysis.setCompareAllMethods}
          krRunMode={analysis.krRunMode}
          onKrRunModeChange={analysis.setKrRunMode}
          onOpenModal={() => setModalOpen(true)}
          onLoadGroup={analysis.setSelection}
          onClear={analysis.clearSelection}
          disabled={running}
        />
      </div>

      {hasSelection ? (
        <>
          <ThumbnailGrid
            samples={analysis.visibleSamples}
            results={analysis.results}
            checkedUids={analysis.checkedUids}
            preexistingUids={analysis.preexistingUids}
            statusFilter={analysis.statusFilter}
            statusCounts={analysis.statusCounts}
            totalCount={analysis.methodSamples.length}
            columns={columns}
            method={analysis.method}
            chartScale={chartScale}
            pceValue={analysis.pceValue}
            onPceChange={analysis.setPce}
            onFilterChange={analysis.setStatusFilter}
            onColumnsChange={setColumns}
            onScaleChange={setChartScale}
            onToggleChecked={analysis.toggleChecked}
            onInspect={analysis.setInspectedUid}
          />

          <BatchControlsBar
            batchStatus={analysis.batchStatus}
            progress={analysis.progress}
            checkedCount={analysis.checkedCount}
            failedCount={analysis.failedUids.length}
            savableCount={analysis.savableUids.length}
            aggregateFit={analysis.aggregateFit}
            method={analysis.method}
            visibleCount={analysis.visibleSamples.length}
            onCheckAll={analysis.checkAll}
            onCheckNone={analysis.checkNone}
            onCheckVisible={analysis.checkVisible}
            onRun={analysis.runChecked}
            onCancel={analysis.cancel}
            onRerunFailed={analysis.rerunFailed}
            onSave={handleSave}
          />

          {hasSavedResults && !running ? (
            <AutoCorrelationCta projectUid={projectUid} />
          ) : null}
        </>
      ) : (
        <div className={styles.emptyState}>
          <EmptyChartIllustration style={{ width: '420px' }} />
          <p className={styles.emptyTitle}>No samples selected</p>
          <p className={styles.emptySubtitle}>
            Select multiple samples from your project to run the Liao or
            Brooks-Corey capillary pressure analysis in batch and validate
            each result.
          </p>
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            Select Samples
          </Button>
        </div>
      )}

      <LoadSampleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleSelectSample}
        onSelectMany={handleSelectMany}
        projectUid={projectUid}
      />

      <Modal
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        title="Overwrite existing analyses?"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmSaveOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmSave}>
              Save and overwrite
            </Button>
          </>
        }
      >
        <p>
          {analysis.overwriteUids.length} of the {analysis.savableUids.length}{' '}
          sample(s) you are about to save already have stored{' '}
          {METHOD_LABELS[analysis.method]} results. Saving will overwrite
          those analyses — including any manual refinements made on the
          Capillary Pressure page.
        </p>
      </Modal>

      <Modal
        open={Boolean(inspectedSample)}
        onClose={() => analysis.setInspectedUid(null)}
        title={
          inspectedSample
            ? `${inspectedSample.sample_code} · ${inspectedSample.depth}${inspectedSample.depth_unit ?? 'ft'}`
            : ''
        }
        size="lg"
      >
        {inspectedSample ? (
          <SampleDetailPanel
            sample={inspectedSample}
            result={analysis.results.get(inspectedSample.uid)}
            method={analysis.method}
            onPrev={inspectedIndex > 0 ? () => stepInspection(-1) : undefined}
            onNext={
              inspectedIndex >= 0 &&
              inspectedIndex < analysis.visibleSamples.length - 1
                ? () => stepInspection(1)
                : undefined
            }
            onRerun={uid => analysis.rerun([uid])}
            onToggleReviewed={analysis.toggleReviewed}
            onOpenInCapillaryPressure={analysis.openInCapillaryPressure}
            regenerateCurves={regenerateCurves}
            rerunDisabled={running}
            methodResults={
              analysis.method === 'lambda'
                ? analysis.methodResultsFor(inspectedSample.uid)
                : undefined
            }
            onChooseMethod={analysis.chooseMethod}
          />
        ) : null}
      </Modal>

      <HelpWindow
        topics={multiAnalysisHelpTopics}
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </ProjectShell>
  );
};

export default MultiAnalysisPage;
